import React, { useState, useEffect, useRef } from "react";
import { Button } from "react-bootstrap";
import { Pencil, Trash2, Plus, ChevronDown, SearchIcon, Check } from "lucide-react";
import CustomeInfoTooltip from "../../common/CustomeInfoTooltip";
import { COMPETENCY_DESCRIPTIONS, COMPETENCY_DESCRIPTIONS_BY_LANGUAGE } from "../../../data/CompetenciesData";
import styles from "../../../_css/AddRoleplay.module.css";
import { COMPETENCIES_BY_LANGUAGE } from "../../../data/competencyData";

const GoalsAndCompetencies = ({
    handleNext,
    step = 4,
    handleBack,
    previousSelections = {},
    updateSelection,
    roleplay_id,
    isLoading,
    language
}) => {
    // --- UI State ---
    const [showDropdown, setShowDropdown] = useState(false);
    const [search, setSearch] = useState("");

    // --- Data State ---
    const [competenciesList, setCompetenciesList] = useState([]);
    const [selectedCompetencies, setSelectedCompetencies] = useState([]);
    const [learningGoals, setLearningGoals] = useState([]);

    // --- Editing State ---
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingValue, setEditingValue] = useState("");

    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    const MOCK_COMPETENCIES = COMPETENCIES_BY_LANGUAGE.English;

    // --- 1. Load Data on Mount Only ---
    useEffect(() => {
        setCompetenciesList(MOCK_COMPETENCIES);

        if (previousSelections && previousSelections[`step${step}`]) {
            let stepData = previousSelections[`step${step}`];

            if (typeof stepData === 'string') {
                try { stepData = JSON.parse(stepData); }
                catch (error) { console.error("Error parsing step data:", error); stepData = {}; }
            }

            if (stepData.learning_goals && Array.isArray(stepData.learning_goals) && stepData.learning_goals.length > 0) {
                setLearningGoals(stepData.learning_goals);
            } else {
                setLearningGoals(["Stay calm in difficult situation"]);
            }

            const savedCompetencies = stepData.competencies || stepData.selected_competencies;

            if (savedCompetencies && Array.isArray(savedCompetencies)) {
                setSelectedCompetencies(savedCompetencies);
            } else {
                setSelectedCompetencies([]);
            }
        } else {
            setLearningGoals(["Stay calm in difficult situation"]);
            setSelectedCompetencies([]);
        }
    }, [step]);

    // --- 2. Event Listeners ---
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => { document.removeEventListener("mousedown", handleClickOutside); };
    }, []);

    useEffect(() => {
        if (editingIndex !== null && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editingIndex]);

    // --- 3. Goals Logic ---
    const addGoal = () => {
        const newGoals = [...learningGoals, ""];
        setLearningGoals(newGoals);
        setEditingIndex(newGoals.length - 1);
        setEditingValue("");
    };

    const removeGoal = (index) => {
        const newGoals = learningGoals.filter((_, i) => i !== index);
        setLearningGoals(newGoals);

        if (index === editingIndex) {
            setEditingIndex(null);
        } else if (editingIndex !== null && index < editingIndex) {
            setEditingIndex(editingIndex - 1);
        }
    };

    const startEditing = (index) => {
        setEditingIndex(index);
        setEditingValue(learningGoals[index]);
    };

    const saveEdit = (index) => {
        const trimmedValue = editingValue.trim();
        if (trimmedValue === "") {
            removeGoal(index);
        } else {
            const updatedGoals = [...learningGoals];
            updatedGoals[index] = trimmedValue;
            setLearningGoals(updatedGoals);
        }
        setEditingIndex(null);
        setEditingValue("");
    };

    const cancelEdit = () => {
        if (editingIndex !== null && learningGoals[editingIndex] === "") {
            removeGoal(editingIndex);
        }
        setEditingIndex(null);
        setEditingValue('');
    };

    // --- 4. Data Preparation & Navigation ---
    const prepareData = () => {
        let finalGoals = [...learningGoals];

        if (editingIndex !== null && editingValue.trim() !== "") {
            finalGoals[editingIndex] = editingValue.trim();
        } else if (editingIndex !== null && editingValue.trim() === "") {
            finalGoals = finalGoals.filter((_, i) => i !== editingIndex);
        }

        finalGoals = finalGoals.filter(g => g && g.trim() !== "");

        return {
            learning_goals: finalGoals,
            competencies: selectedCompetencies,
            selected_competencies: selectedCompetencies
        };
    };

    const onNext = () => {
        const stepData = prepareData();
        if (stepData.learning_goals.length > 0 && stepData.competencies.length > 0) {
            updateSelection(step, stepData);
            handleNext(stepData);
        } else {
            console.warn("Validation failed: select at least 1 goal and 1 competency");
        }
    };

    const onBack = () => {
        const stepData = prepareData();
        updateSelection(step, stepData);
        handleBack();
    };

    // --- 5. Competency Selection Handler ---
    const toggleCompetency = (name) => {
        const isSelected = selectedCompetencies.includes(name);
        if (!isSelected && selectedCompetencies.length >= 3) return;

        if (isSelected) {
            setSelectedCompetencies(prev => prev.filter(item => item !== name));
        } else {
            setSelectedCompetencies(prev => [...prev, name]);
        }
    };

    return (
        <div className={styles.stepCard}>
            {/* Header */}
            <div className={styles.headerContainer}>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", width: "90%" }}>
                    <div className={styles.headerContent} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <h4 className={styles.title}>Learning Goals & Competencies</h4>
                        <div style={{ marginTop: "-4px" }}>
                            <CustomeInfoTooltip description="Write each goal in one sentence. Personal goals (e.g. I want to improve summarizing) or criteria you're usually coached on." />
                        </div>
                    </div>
                    <p className={styles.subHeader} style={{ fontSize: '14px' }}>Minimum: 1</p>
                </div>
            </div>

            <p className={styles.subHeader} style={{ marginBottom: '10px', marginTop: "-12px" }}>
                What do you want to achieve in this conversation and which competencies should you be evaluated on?
            </p>

            {/* Goals List */}
            <div style={{ marginTop: "5px" }}>
                {learningGoals.map((goal, index) => (
                    <div
                        key={index}
                        className={styles.goalItem}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            flexWrap: 'nowrap', // 1. Prevents wrapping
                            gap: '10px',        // 2. Creates space between text and icons
                            boxSizing: 'border-box',
                            position: 'relative'
                        }}
                    >
                        {/* Left Side: Number + Text/Input */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            flex: 1,       // 3. Takes up all available space
                            minWidth: 0,   // 4. CRITICAL: Allows this container to shrink if needed
                            overflow: 'hidden'
                        }}>
                            <span className={styles.goalIndex} style={{ marginRight: '8px', flexShrink: 0 }}>
                                {index + 1}.
                            </span>

                            {editingIndex === index ? (
                                <input
                                    className={styles.goalInput}
                                    ref={inputRef}
                                    type="text"
                                    placeholder="Type your goal..."
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    onBlur={() => saveEdit(index)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            saveEdit(index);
                                        }
                                        if (e.key === "Escape") cancelEdit();
                                    }}
                                    style={{
                                        width: '100%', // 5. Fills the flexible container
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent'
                                    }}
                                />
                            ) : (
                                <span
                                    className={styles.goalText}
                                    onClick={() => startEditing(index)}
                                    style={{
                                        cursor: 'pointer',
                                        width: '100%',
                                        display: 'block',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                    title={goal}
                                >
                                    {goal || <span style={{ color: '#999', fontStyle: 'italic' }}>Empty goal...</span>}
                                </span>
                            )}
                        </div>

                        {/* Right Side: Action Buttons */}
                        <div
                            className={styles.iconActions}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                flexShrink: 0, // 6. Ensures buttons never shrink or disappear
                                marginLeft: 'auto'
                            }}
                        >
                            {editingIndex === index ? (
                                <button className={styles.iconButton} onClick={() => saveEdit(index)} title="Save">
                                    <Check size={18} color="green" strokeWidth={3} />
                                </button>
                            ) : (
                                <button className={styles.iconButton} onClick={() => startEditing(index)} title="Edit">
                                    <Pencil size={18} color="#6B7280" />
                                </button>
                            )}

                            <button className={styles.iconButton} onClick={() => removeGoal(index)} title="Delete">
                                <Trash2 size={18} color="#ef4444" />
                            </button>
                        </div>
                    </div>
                ))}

                {learningGoals.length === 0 && (
                    <div className={styles.emptyState} style={{ padding: '10px', color: '#6b7280', fontStyle: 'italic' }}>
                        No goals set. Please add a goal to continue.
                    </div>
                )}

                <div>
                    <button className={styles.addGoalButton} onClick={addGoal} title="Add new goal">
                        <Plus size={24} color="#000000" />
                    </button>
                </div>
            </div>

            {/* Competencies Section */}
            <div className={styles.formGroup} style={{ marginTop: '20px' }}>
                <div className={styles.competencyHeaderRow}>
                    <span className={styles.label}>Competencies (required: min. 1, max. 3)</span>
                    <CustomeInfoTooltip description="Select the compentencies you want to be evaluated on. You'll receive a score and feedback for each." />
                </div>

                <div className={styles.competencyDropdownWrapper} ref={dropdownRef}>
                    <button
                        className={styles.competencyTrigger}
                        onClick={() => setShowDropdown(!showDropdown)}
                    >
                        {selectedCompetencies.length === 0
                            ? "Select Competencies"
                            : selectedCompetencies.join(", ")}
                        <ChevronDown
                            size={20}
                            color="#9ca3af"
                            style={{
                                transform: showDropdown ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.2s",
                            }}
                        />
                    </button>

                    {showDropdown && (
                        <div className={styles.competencyMenu}>
                            <div className={styles.searchContainer}>
                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        type="text"
                                        placeholder="Search competency"
                                        className={styles.searchInput}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    <SearchIcon
                                        color="#6b7280"
                                        size={20}
                                        style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                                    />
                                </div>
                            </div>

                            <div className={styles.competencyList}>
                                {competenciesList
                                    .filter((name) => !search || name.toLowerCase().includes(search.toLowerCase()))
                                    .map((name) => {
                                        const isSelected = selectedCompetencies.includes(name);
                                        const isDisabled = !isSelected && selectedCompetencies.length >= 3;

                                        return (
                                            <div
                                                key={name}
                                                className={`${styles.competencyItem} ${isSelected ? styles.selected : ''} ${isDisabled ? styles.disabled : ''}`}
                                                onClick={() => toggleCompetency(name)}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    readOnly
                                                    className={styles.checkboxInput}
                                                    disabled={isDisabled}
                                                />
                                                <span>{name}</span>
                                                <CustomeInfoTooltip description={
                                                    COMPETENCY_DESCRIPTIONS_BY_LANGUAGE[language]?.[name]
                                                    ?? COMPETENCY_DESCRIPTIONS[name]
                                                    ?? "Standard competency description."
                                                } />
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation */}
            <div className={styles.buttonGroup}>
                <Button variant="light" className={styles.backButton} onClick={onBack} disabled={isLoading}>
                    Back
                </Button>

                <Button
                    className={styles.nextButton}
                    onClick={onNext}
                    disabled={learningGoals.length === 0 || selectedCompetencies.length === 0 || isLoading}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                        </>
                    ) : "Next"}
                </Button>
            </div>
        </div>
    );
};

export default GoalsAndCompetencies;